import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin, Youtube, Eye, ArrowRight, Send } from "lucide-react";
import { useSettings } from "../../lib/useSettings";

export default function Footer() {
  const s = useSettings();
  return (
    <footer className="relative bg-brand-dark text-slate-300 mt-24 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, white 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-brand-primary/15 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-brand-secondary/10 blur-3xl" />

      {/* Newsletter strip */}
      <div className="relative border-b border-white/10">
        <div className="container-wide py-10 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-brand-secondary font-semibold mb-2">Stay Updated</div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-white leading-tight">Society updates, CMEs & announcements — straight to your inbox.</h3>
          </div>
          <form className="flex flex-col sm:flex-row gap-3" onSubmit={(e) => e.preventDefault()}>
            <input type="email" placeholder="Enter your email address" className="flex-1 rounded-full bg-white/10 border border-white/15 px-5 py-3.5 text-white placeholder:text-white/50 focus:outline-none focus:border-brand-secondary text-sm" data-testid="footer-newsletter-input" />
            <button className="btn-primary bg-brand-secondary hover:bg-brand-secondary-hover !py-3.5" data-testid="footer-newsletter-submit">
              Subscribe <Send size={16} />
            </button>
          </form>
        </div>
      </div>

      <div className="container-wide relative py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-3">
            <img src="/gos-logo.png" alt="GOS Logo" className="w-14 h-14 object-contain bg-white rounded-2xl p-1.5" />
            <div>
              <div className="font-display text-white font-bold text-base">Ghaziabad Ophthalmological Society</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400 mt-0.5">Est. {s.established_year || 1983}</div>
            </div>
          </div>
          <p className="mt-6 text-sm text-slate-400 leading-relaxed max-w-sm">
            {s.tagline || "Advancing Excellence in Eye Care, Education & Research."} A professional association of ophthalmologists in Ghaziabad and NCR.
          </p>
          <div className="flex items-center gap-2.5 mt-6">
            {[["facebook", Facebook], ["twitter", Twitter], ["instagram", Instagram], ["linkedin", Linkedin], ["youtube", Youtube]].map(([k, Ico]) =>
              s[k] ? (
                <a key={k} href={s[k]} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 hover:bg-brand-primary hover:border-brand-primary flex items-center justify-center transition-all hover:-translate-y-0.5" aria-label={k}>
                  <Ico size={15} />
                </a>
              ) : null
            )}
          </div>
        </div>
        <div className="lg:col-span-2">
          <h4 className="text-white font-display font-bold text-[15px] mb-5">Society</h4>
          <ul className="space-y-3 text-sm">
            {[
              ["About", "/about"],
              ["Organization Structure", "/executive-committee"],
              ["Members", "/members"],
              ["History", "/history"],
            ].map(([l, to]) => (
              <li key={to}><Link to={to} className="text-slate-400 hover:text-brand-secondary transition-colors inline-flex items-center gap-1.5 group">{l} <ArrowRight size={11} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" /></Link></li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-2">
          <h4 className="text-white font-display font-bold text-[15px] mb-5">Programs</h4>
          <ul className="space-y-3 text-sm">
            {[
              ["Announcements", "/announcements"],
              ["Events", "/events"],
              ["CME Programs", "/cme-programs"],
              ["Gallery", "/gallery"],
              ["Downloads", "/downloads"],
            ].map(([l, to]) => (
              <li key={to}><Link to={to} className="text-slate-400 hover:text-brand-secondary transition-colors inline-flex items-center gap-1.5 group">{l} <ArrowRight size={11} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" /></Link></li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-4">
          <h4 className="text-white font-display font-bold text-[15px] mb-5">Get in Touch</h4>
          <ul className="space-y-4 text-sm">
            <li className="flex gap-3"><div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><MapPin size={14} className="text-brand-secondary" /></div><span className="text-slate-400 leading-relaxed">{s.address}</span></li>
            <li className="flex gap-3"><div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><Phone size={14} className="text-brand-secondary" /></div><a href={`tel:${s.phone}`} className="text-slate-400 hover:text-white transition-colors">{s.phone}</a></li>
            <li className="flex gap-3"><div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><Mail size={14} className="text-brand-secondary" /></div><a href={`mailto:${s.email}`} className="text-slate-400 hover:text-white transition-colors break-all">{s.email}</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 relative">
        <div className="container-wide py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <div className="flex items-center gap-2"><Eye size={13} className="text-brand-secondary" /> © {new Date().getFullYear()} Ghaziabad Ophthalmological Society. All rights reserved.</div>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link>
            <div>Designed with care for GOS</div>
          </div>
        </div>
      </div>
    </footer>
  );
}
